import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { formatChartDate } from '../utils/dateUtils';
import { useAuth } from '../context/AuthContext';
import BloodTrendChart from './shared/BloodTrendChart';
import SupplementTimeline from './SupplementTimeline';
import { computePageDomain } from '../utils/resultsDomain';
import TimeWindowSelector, { filterByTimeWindow, filterTreatmentsByTimeWindow } from './TimeWindowSelector';

/**
 * NutritionGraphs - Vitamin/nutrition marker chart with treatment timeline
 * 
 * CRITICAL: X-axis domain is computed from FILTERED results only.
 * Time window filtering is presentation-layer only.
 * Tabs change Y-data only — X-axis is SHARED across all tabs.
 * 
 * STICKY TABS: Uses pure CSS position:sticky for smooth, jitter-free behavior.
 * Structure copied from BloodGraphs for consistency.
 */
const DashboardNutritionGraphs = ({ records, supplements = [] }) => {
  const { user } = useAuth();
  const country = user?.country || 'AU';
  const [activeMarker, setActiveMarker] = useState('vitamin_d');
  const [timeWindow, setTimeWindow] = useState('36m');
  const [chartAxisConfig, setChartAxisConfig] = useState(null);
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  
  // Refs for synchronized scrolling
  const chartScrollRef = useRef(null);
  const timelineScrollRef = useRef(null);
  const isScrolling = useRef(false);
  const isInitialScrollDone = useRef(false);
  
  // Auto-scroll chart to the right (most recent date) on mount AND when time window changes
  useEffect(() => {
    setShowSwipeHint(true);
    isInitialScrollDone.current = false;
    
    const scrollToRight = () => {
      const chartContainer = chartScrollRef.current;
      const timelineContainer = timelineScrollRef.current;
      
      if (chartContainer) {
        chartContainer.scrollLeft = chartContainer.scrollWidth;
      }
      if (timelineContainer) {
        timelineContainer.scrollLeft = timelineContainer.scrollWidth;
      }
      
      setTimeout(() => {
        isInitialScrollDone.current = true;
      }, 300);
    };
    
    const timer = setTimeout(scrollToRight, 150);
    return () => clearTimeout(timer);
  }, [timeWindow]);
  
  // Synchronized horizontal scroll handler
  const handleHorizontalScroll = useCallback((source) => {
    if (isScrolling.current) return;
    isScrolling.current = true;
    
    const chartContainer = chartScrollRef.current;
    const timelineContainer = timelineScrollRef.current;
    
    if (isInitialScrollDone.current && chartContainer) {
      const maxScroll = chartContainer.scrollWidth - chartContainer.clientWidth;
      const isAtEnd = chartContainer.scrollLeft >= maxScroll - 50;
      const isScrolledAway = chartContainer.scrollLeft < maxScroll - 100;
      
      if (isAtEnd) {
        setShowSwipeHint(true);
      } else if (isScrolledAway) {
        setShowSwipeHint(false);
      }
    }
    
    if (chartContainer && timelineContainer) {
      if (source === 'chart') {
        timelineContainer.scrollLeft = chartContainer.scrollLeft;
      } else {
        chartContainer.scrollLeft = timelineContainer.scrollLeft;
      }
    }
    
    setTimeout(() => {
      isScrolling.current = false;
    }, 50);
  }, []);

  // Filter records and treatments by time window
  const filteredRecords = useMemo(() => {
    return filterByTimeWindow(records, timeWindow);
  }, [records, timeWindow]);

  const filteredSupplements = useMemo(() => {
    return filterTreatmentsByTimeWindow(supplements, records, timeWindow);
  }, [supplements, records, timeWindow]);

  // Compute X-axis domain
  const pageDomain = useMemo(() => {
    return computePageDomain(records, supplements, country, timeWindow);
  }, [records, supplements, country, timeWindow]);

  const getMarkers = () => {
    const isUS = country === 'US';
    return [
      { key: 'vitamin_d', label: 'Vitamin D', color: '#0F766E', unit: isUS ? 'ng/mL' : 'nmol/L', range: isUS ? { min: 20, max: 50 } : { min: 50, max: 125 } },
      { key: 'b12', label: 'Vitamin B12', color: '#059669', unit: isUS ? 'pg/mL' : 'pmol/L', range: isUS ? { min: 200, max: 900 } : { min: 150, max: 700 } },
      { key: 'ferritin', label: 'Ferritin', color: '#0369A1', unit: 'ng/mL', range: { min: 30, max: 300 } },
      { key: 'folate', label: 'Folate', color: '#D97706', unit: isUS ? 'ng/mL' : 'nmol/L', range: isUS ? { min: 3, max: 20 } : { min: 7, max: 45 } }
    ];
  };

  const markers = getMarkers();

  const hasDataForMarker = (markerKey) => {
    return filteredRecords.some(r => r[markerKey] != null);
  };

  const sortedMarkers = [...markers].sort((a, b) => {
    const aHasData = hasDataForMarker(a.key);
    const bHasData = hasDataForMarker(b.key);
    if (aHasData && !bHasData) return -1;
    if (!aHasData && bHasData) return 1;
    return 0;
  });

  const markerConfig = markers.find(m => m.key === activeMarker);

  // Prepare chart data - data within window + ONE point before for line continuity
  const chartData = useMemo(() => {
    if (!pageDomain) return [];
    
    const domainStart = pageDomain.domainStartTs;
    const domainEnd = pageDomain.domainEndTs;
    
    const allData = records
      .filter(r => r[activeMarker] != null)
      .map(r => ({
        date: formatChartDate(r.date, country),
        fullDate: r.date,
        timestamp: new Date(r.date).getTime(),
        value: r[activeMarker]
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
    
    if (allData.length === 0) return [];
    
    const windowData = allData.filter(d => d.timestamp >= domainStart && d.timestamp <= domainEnd);
    const pointBefore = allData.filter(d => d.timestamp < domainStart).pop();
    
    if (pointBefore && windowData.length > 0) {
      return [pointBefore, ...windowData];
    }
    
    return windowData.length > 0 ? windowData : [];
  }, [records, pageDomain, activeMarker, country]);

  const hasData = chartData.length > 0;

  // Tabs content - rendered inline to avoid nested component issues
  const tabsContent = (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 -mx-1 px-1">
        {sortedMarkers.map(marker => {
          const markerHasData = hasDataForMarker(marker.key);
          return (
            <button
              key={marker.key}
              onClick={() => setActiveMarker(marker.key)}
              className={`px-3 py-2 md:px-4 md:py-2 rounded-lg transition-all whitespace-nowrap text-sm md:text-base flex-shrink-0 ${
                activeMarker === marker.key
                  ? 'bg-primary/10 text-primary border-2 border-primary font-semibold md:font-medium'
                  : markerHasData
                    ? 'bg-white text-slate-600 border-2 border-slate-200 hover:border-slate-300 font-medium'
                    : 'bg-slate-50 text-slate-400 border-2 border-slate-100 hover:border-slate-200 font-medium'
              }`}
            >
              {marker.label}
            </button>
          );
        })}
      </div>
      <div className="flex-shrink-0 self-end sm:self-auto">
        <TimeWindowSelector selected={timeWindow} onChange={setTimeWindow} />
      </div>
    </div>
  );

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Sticky Tabs - Pure CSS position:sticky for smooth, jitter-free behavior */}
      <div 
        className="bg-slate-50 border-b border-slate-200 -mx-4 px-4 py-2 md:-mx-8 md:px-8 lg:-mx-12 lg:px-12 sticky z-40"
        style={{ 
          top: 'var(--sticky-top, 0px)',
          boxShadow: '0 2px 8px -2px rgba(0, 0, 0, 0.1)'
        }}
      >
        <style>{`
          /* Normal logged-in site - mobile header is ~44px */
          @media (max-width: 1023px) {
            :root { --sticky-top: 44px; }
          }
          @media (min-width: 1024px) {
            :root { --sticky-top: 0px; }
          }
          /* Preview site - account for the fixed preview banner (~32px) + header (~44px) */
          body.preview-layout {
            --sticky-top: 76px;
          }
          @media (min-width: 1024px) {
            body.preview-layout {
              --sticky-top: 32px;
            }
          }
        `}</style>
        {tabsContent}
      </div>

      {/* Chart Container */}
      <div className="bg-white rounded-t-xl p-4 md:p-6 border-t border-x border-slate-200">
        <div className="mb-3 md:mb-4 flex items-start justify-between">
          <div>
            <div className="flex items-baseline gap-2">
              <h3 className="text-base md:text-lg font-semibold text-slate-900">{markerConfig.label} Trend</h3>
              <span className="text-sm text-slate-500">({markerConfig.unit})</span>
            </div>
            <p className="text-xs md:text-sm text-slate-500">
              Reference range: {markerConfig.range.min}-{markerConfig.range.max}
            </p>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-teal-600 bg-teal-50 px-2 py-1 rounded-full">
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
            <span>Live • {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          </div>
        </div>
        
        {/* Chart Legend */}
        <div className="mb-3 flex items-center gap-4 text-xs text-slate-600">
          <div className="flex items-center gap-1.5">
            <div 
              className="w-4 h-3 rounded-sm" 
              style={{ backgroundColor: markerConfig.color, opacity: 0.15 }}
            ></div>
            <span>Reference range</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div 
              className="w-4 h-0.5 rounded-full" 
              style={{ backgroundColor: markerConfig.color }}
            ></div>
            <span>Your results</span>
          </div>
        </div>

        {/* Scrollable Chart Container */}
        <div className="relative">
          <div 
            ref={chartScrollRef}
            onScroll={() => handleHorizontalScroll('chart')}
            className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div style={{ minWidth: pageDomain ? '600px' : '100%' }}>
              <BloodTrendChart
                data={chartData}
                markerConfig={markerConfig}
                showLegend={true}
                pageDomain={pageDomain}
                onDataReady={setChartAxisConfig}
              />
            </div>
          </div>
          
          {/* Mobile swipe hint - minimal fixed height to prevent layout shift */}
          <div className="lg:hidden h-7">
            {showSwipeHint && (
              <div className="flex items-center justify-center gap-2 py-1 bg-slate-100 rounded-md text-slate-500 border border-slate-200 h-full mt-0.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                </svg>
                <span className="text-[11px] font-medium">Swipe to see full chart</span>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Empty State Message - shows if no data */}
        {!hasData && (
          <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-slate-200 text-center">
            <p className="text-xs md:text-sm text-slate-600">No results recorded yet for this marker.</p>
          </div>
        )}
      </div>

      {/* Treatment Timeline - Uses SAME filtered domain - Synced scrolling - Close to chart */}
      {pageDomain && (
        <div 
          ref={timelineScrollRef}
          onScroll={() => handleHorizontalScroll('timeline')}
          className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 -mt-1"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div style={{ minWidth: '600px' }}>
            <SupplementTimeline
              supplements={filteredSupplements}
              axisConfig={chartAxisConfig || {
                domainStartTs: pageDomain.domainStartTs,
                domainEndTs: pageDomain.domainEndTs,
                ticks: [],
                margin: { left: 40, right: 0 }
              }}
            />
          </div>
        </div>
      )}
      
      {/* Disclaimer - shows AFTER treatments section on ALL screen sizes */}
      {hasData && (
        <div className="mt-3 pt-3 border-t border-slate-200">
          <p className="text-xs text-slate-500 text-center leading-relaxed">
            Reference ranges are clinical guidelines. Charts show historical trends for tracking purposes only.
          </p>
        </div>
      )}
    </div>
  );
};

export default DashboardNutritionGraphs;
